import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { useLoaderContext } from '../contexts/loader';
import { usePopUpContext } from '../contexts/Popup';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useUserContext } from '../contexts/user';
import { ref, update } from 'firebase/database';
import { db } from '../Database';
import isArabic from 'is-arabic';
import { useResultContext } from '../contexts/Results';
import { useCheckForNewToken } from '../checkForNewToken';

const QuizPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const { checkForNewToken } = useCheckForNewToken();

    const { setIsLoading } = useLoaderContext();
    const { setMessageContent } = usePopUpContext();
    const { userInfo, userIndex, setUserInfo, userJWT, setUserJWT, setUser, setUserIndex, appLanguage } = useUserContext();
    const { setUserResult } = useResultContext();

    const [quizData, setQuizData] = useState({});
    const [questionIndex, setQuestionIndex] = useState(0);
    const [choosedAnswers, setChoosedAnswers] = useState([]);
    const [showSubmit, setShowSubmit] = useState(false);
    const [remainingTime, setRemainingTime] = useState({ minutes: 0, seconds: 0, });

    const interval = useRef(null);
    const userChoosedAnswers = useRef([]);

    useEffect(() => {
        if (userInfo.quizEndsAt) {
            interval.current = setInterval(() => {
                const remainingMinutes = Math.floor((userInfo.quizEndsAt - new Date().getTime()) / 1000 / 60);
                const remainingSeconds = Math.floor((userInfo.quizEndsAt - new Date().getTime()) / 1000 % 60);
                if (remainingMinutes < 1 && remainingSeconds < 1) {
                    finish();
                    clearInterval(interval.current);
                } else {
                    setRemainingTime({ minutes: remainingMinutes, seconds: remainingSeconds, });
                }
            }, 1000)
        } else {
            clearInterval(interval.current);
        }
        return () => clearInterval(interval.current);
    }, [quizData])

    useEffect(() => {
        setIsLoading(prev => prev + 1);
        if (userInfo.role && (userInfo.playedQuizzes ? !userInfo.playedQuizzes.includes(id) : true)) {
            if (userInfo.role && userInfo.role === "student") {
                if (userInfo.playingQuiz && userInfo.playingQuiz === id) {
                    setChoosedAnswers(userInfo.choosedAnswers || []);
                    userChoosedAnswers.current = userInfo.choosedAnswers || [];
                    fetch("https://quiz-battle-api.vercel.app/api/quiz/no-correct-answer/" + id, {
                        method: "GET",
                        credentials: "include",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${userJWT}`,
                        },
                    }).then(res => res.json())
                        .then(data => {
                            checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
                            setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                            if (data.deleted) {
                                update(ref(db, "users/" + userInfo.id), {
                                    quizEndsAt: null,
                                    choosedAnswers: [],
                                    playingQuiz: null,
                                }).then(() => {
                                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                                    navigate("/home");
                                    setMessageContent({
                                        title: appLanguage === "ar" ? "إختبار محذوف" : "Deleted Quiz",
                                        message: appLanguage === "ar" ? "تم حذف هذا الإختبار من قِبَل المدرس." : "This quiz has been deleted by the teacher.",
                                    })
                                }).catch(() => {
                                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                                    navigate("/home");
                                    setMessageContent({
                                        title: appLanguage === "ar" ? "إختبار محذوف" : "Deleted Quiz",
                                        message: appLanguage === "ar" ? "تم حذف هذا الإختبار من قِبَل المدرس." : "This quiz has been deleted by the teacher.",
                                    })
                                })
                            }
                            if (data.failed) {
                                if (!data.deleted) {
                                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                                    setMessageContent({
                                        title: appLanguage === "ar" ? "فشل جلب البيانات" : "Failed To Fetch",
                                        message: data.msg,
                                    })
                                    navigate("/home")
                                }
                            } else {
                                setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                                const questions = [...data.questions];
                                const newQuestions = questions.map(question => {
                                    const answers = [...question.answers];
                                    const newAnswers = [];
                                    while (newAnswers.length !== 4) {
                                        const randomIndex = Math.floor(Math.random() * answers.length);
                                        newAnswers.push(answers[randomIndex]);
                                        answers.splice(randomIndex, 1);
                                    }
                                    return { ...question, answers: newAnswers, };
                                })
                                setQuizData({ ...data, questions: newQuestions, });
                            }
                        }).catch(err => {
                            setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                            console.log(err);
                            navigate("/home");
                            setMessageContent({
                                title: appLanguage === "ar" ? "فشل جلب البيانات" : "Failed To Fetch",
                                message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء جلب بيانات الاختبار." : "Oops! something went wrong while fetching the quiz from the database",
                            })
                        })
                } else {
                    navigate("/info/" + id);
                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                }
            } else {
                if (userInfo.role === "teacher") {
                    navigate("/home");
                    setMessageContent({
                        title: appLanguage === "ar" ? "تم رفض الوصول" : "Access Denied",
                        message: appLanguage === "ar" ? "يمكن للطلاب فقط حل الاختبارات." : "Only the students can solve the quizzes.",
                    });
                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                } else {
                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
                }
            }
        } else {
            if (userInfo.role && userInfo.playedQuizzes && userInfo.playedQuizzes.includes(id)) {
                setMessageContent({
                    title: appLanguage === "ar" ? "تم حل الاختبار" : "Solved Quiz",
                    message: appLanguage === "ar" ? "لقد قمت بحل هذا الاختبار من قبل لذا لا يمكنك حله مرة أخرى." : "You have solve this quiz before so you can't solve it again.",
                })
                navigate("/result/" + id);
                setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
            } else {
                setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
            }
        }
        return () => clearInterval(interval.current);
    }, [userIndex])

    const changeAnswer = (index) => {
        if (quizData.questions && new Date().getTime() <= userInfo.quizEndsAt) {
            const updatedAnswers = [...choosedAnswers];
            updatedAnswers[questionIndex] = quizData.questions[questionIndex].answers[index];

            for (let i = 0; i < quizData.questions.length; i++) {
                if (!updatedAnswers[i]) {
                    updatedAnswers[i] = { empty: "true" };
                }
            }

            setChoosedAnswers(updatedAnswers);
            userChoosedAnswers.current = updatedAnswers;

            setUserInfo(prev => ({
                ...prev,
                choosedAnswers: updatedAnswers,
            }));

            update(ref(db, "users/" + userInfo.id), {
                choosedAnswers: updatedAnswers,
            });
        }
    };

    const finish = () => {
        setIsLoading(prev => prev + 1);
        if (!userInfo.playedQuizzes || !userInfo.playedQuizzes.includes(id)) {
            fetch("https://quiz-battle-api.vercel.app/api/submit", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${userJWT}`,
                },
            }).then(res => res.json())
                .then(data => {
                    checkForNewToken(data, setUserJWT, setUserInfo, setUser, setUserIndex, navigate, setMessageContent);
                    if (data.saved) {
                        setMessageContent({
                            title: appLanguage === "ar" ? "انتهى الاختبار" : "Quiz Finished",
                            message: appLanguage === "ar" ? "تم حفظ الاختبار و تم إرسالة إلى المدرس" : "The quiz has been saved and sent to the teacher",
                        })
                        setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                        navigate("/result/" + id);
                    } else {
                        setMessageContent({
                            title: appLanguage === "ar" ? "فشل الإرسال" : "Submitting Failed",
                            message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء حفظ نتيجتك." : "Oops! something wrong occured while saving your result.",
                        })
                    }
                }).catch(err => {
                    console.log(err);
                    setMessageContent({
                        title: appLanguage === "ar" ? "فشل الإرسال" : "Submitting Failed",
                        message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء حفظ نتيجتك." : "Oops! something wrong occured while saving your result.",
                    })
                    setIsLoading(prev => prev !== 0 ? prev - 1 : prev);

                })
        } else {
            navigate("/result/" + id);
            setIsLoading(prev => prev !== 0 ? prev - 1 : prev);
            clearInterval(interval.current)
        }
    }

    return (
        <div className="quiz_page_container">
            <header>
                <p className="title">{quizData.category} {appLanguage === "ar" ? "اختبار" : "Quiz"}</p>
                <p className="timer"><Clock /> <b>{userInfo.quizEndsAt ? `${remainingTime.minutes < 10 ? "0" + remainingTime.minutes : remainingTime.minutes}:${remainingTime.seconds < 10 ? "0" + remainingTime.seconds : remainingTime.seconds}` : "--:--"}</b></p>
            </header>
            <div className="question_container">
                <div className="content">
                    <div className="question_text" dir={quizData.questions ? (isArabic(quizData.questions[questionIndex].question.split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{quizData.questions ? quizData.questions[questionIndex].question : (appLanguage === "ar" ? "جاري جلب الأسئلة" : "Fetching Questions")}</div>
                    <div className="answers">
                        <button onClick={() => changeAnswer(0)} className={"answer " + (choosedAnswers[questionIndex] === (quizData.questions ? quizData.questions[questionIndex].answers[0] : "") ? "active" : "")} dir={quizData.questions ? (isArabic(quizData.questions[questionIndex].answers[0].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{quizData.questions ? quizData.questions[questionIndex].answers[0] : (appLanguage === "ar" ? "جاري الجلب" : "Fetching")}</button>
                        <button onClick={() => changeAnswer(1)} className={"answer " + (choosedAnswers[questionIndex] === (quizData.questions ? quizData.questions[questionIndex].answers[1] : "") ? "active" : "")} dir={quizData.questions ? (isArabic(quizData.questions[questionIndex].answers[1].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{quizData.questions ? quizData.questions[questionIndex].answers[1] : (appLanguage === "ar" ? "جاري الجلب" : "Fetching")}</button>
                        <button onClick={() => changeAnswer(2)} className={"answer " + (choosedAnswers[questionIndex] === (quizData.questions ? quizData.questions[questionIndex].answers[2] : "") ? "active" : "")} dir={quizData.questions ? (isArabic(quizData.questions[questionIndex].answers[2].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{quizData.questions ? quizData.questions[questionIndex].answers[2] : (appLanguage === "ar" ? "جاري الجلب" : "Fetching")}</button>
                        <button onClick={() => changeAnswer(3)} className={"answer " + (choosedAnswers[questionIndex] === (quizData.questions ? quizData.questions[questionIndex].answers[3] : "") ? "active" : "")} dir={quizData.questions ? (isArabic(quizData.questions[questionIndex].answers[3].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{quizData.questions ? quizData.questions[questionIndex].answers[3] : (appLanguage === "ar" ? "جاري الجلب" : "Fetching")}</button>
                    </div>
                </div>
            </div>
            <div className="controls">
                <button onClick={() => { questionIndex !== 0 ? setQuestionIndex(prev => prev - 1) : console.clear() }} className={questionIndex === 0 ? "disabled" : ""}><ChevronLeft /> {appLanguage === "ar" ? "السابق" : "Prev"}</button>
                <div className="question_index">{questionIndex + 1 < 10 ? "0" + (questionIndex + 1) : questionIndex + 1}/{quizData.questions ? (quizData.questions.length < 10 ? "0" + quizData.questions.length : quizData.questions.length) : "00"}</div>
                <button onClick={() => { questionIndex + 1 !== (quizData.questions ? quizData.questions.length : 0) ? setQuestionIndex(prev => prev + 1) : setShowSubmit(true) }} className={questionIndex + 1 === (quizData.questions ? quizData.questions.length : 0) ? "finish" : ""}>{questionIndex + 1 === (quizData.questions ? quizData.questions.length : 0) ? (appLanguage === "ar" ? "إنهاء" : "Finish") : <>{appLanguage === "ar" ? "التالي" : "Next"} <ChevronRight /></>}</button>
            </div>
            {showSubmit ? <SubmitMessageAlert finish={finish} setShowSubmit={setShowSubmit} appLanguage={appLanguage} /> : undefined}
        </div>
    )
}

const SubmitMessageAlert = ({ finish, setShowSubmit, appLanguage }) => {
    return (
        <div className="confirm_message">
            <div className="content">
                <p>{appLanguage === "ar" ? "هل تريد إنهاء الاختبار وإرساله الآن أم تريد التحقق من إجاباتك؟" : "Do you want to finish and submit the quiz now or you want to double check your answers?"}</p>
                <div className="buttons">
                    <button id="publish" onClick={() => finish()}>{appLanguage === "ar" ? "إنهاء" : "Finish"}</button>
                    <button id="check" onClick={() => setShowSubmit(false)}>{appLanguage === "ar" ? "تحقق من الإجابات" : "Double Check"}</button>
                </div>
            </div>
        </div>
    );
}

export default QuizPage;